// BEGIN FILE: src/astro/feeds/scoring.ts
import type { PlanetName } from '../../utils/astroEngine';
import type { HouseNum, TransitContext } from './types';
import { PLANET_NATURE } from './types';

export function weightByLevel(level: 'MD'|'AD'|'PD'|'SD'): number {
  switch (level) {
    case 'MD': return 1.0;
    case 'AD': return 0.7;
    case 'PD': return 0.4;
    case 'SD': return 0.2;
  }
}

export function natureWeight(planet: PlanetName): number {
  const nature = PLANET_NATURE[planet];
  if (nature === 'benefic') return +1;
  if (nature === 'malefic') return -1;
  return +0.5; // conditional default
}

export function normHouseScore(h: HouseNum, scores: Record<number, number>): number {
  const v = scores[h] ?? 0;
  // map −100..+100 → −1..+1
  const n = Math.max(-100, Math.min(100, v)) / 100;
  return n;
}

function addWrap(h: HouseNum, delta0: number): HouseNum {
  const z = (((h - 1) + delta0) % 12 + 12) % 12; // 0..11
  return (z + 1) as HouseNum;
}

export type ReinforceArgs = {
  targetHouse: HouseNum;
  transits: TransitContext;
  forLifeArea?: string;
};

/** Tiny, documented constants so we can tune later. */
const JUPITER_BONUS = +0.15;     // +15 when Jupiter sits/trines
const SATURN_PENALTY = -0.15;    // −15 when Saturn sits/aspects
const RISK_PENALTY = -0.20;      // −20 when Mars/Nodes cross 1/6/8/12 (risk/health)
const ACTIVATION_BONUS = +0.10;  // +10 if any planet transits any active dasha occupied house

export function transitReinforcement({ targetHouse, transits, forLifeArea }: ReinforceArgs): number {
  let acc = 0;
  const tri = [targetHouse, addWrap(targetHouse, 4), addWrap(targetHouse, 8)]; // target, +4, +8
  const riskHouses: HouseNum[] = [1,6,8,12];

  // Aggregate across potentially many position samples (e.g., over the active window)
  let jupiterTriHit = false;
  let saturnTargetHit = false;
  let riskHit = false;

  for (const p of transits.positions) {
    if (!jupiterTriHit && p.planet === 'Jupiter') {
      if (tri.includes(p.house)) jupiterTriHit = true;
    }
    if (!saturnTargetHit && p.planet === 'Saturn') {
      const satHits = new Set([p.house, ...(p.aspects||[])] as HouseNum[]);
      if (satHits.has(targetHouse)) saturnTargetHit = true;
    }
    if (!riskHit && (p.planet === 'Mars' || p.planet === 'Rahu' || p.planet === 'Ketu')) {
      if (forLifeArea === 'AccidentRisk' || forLifeArea === 'Health' || forLifeArea === 'Self') {
        if (riskHouses.includes(p.house)) riskHit = true;
      }
    }
    // Early exit if all gathered
    if (jupiterTriHit && saturnTargetHit && riskHit) break;
  }

  if (jupiterTriHit) acc += JUPITER_BONUS;
  if (saturnTargetHit) acc += SATURN_PENALTY;
  if (riskHit) acc += RISK_PENALTY;

  // Activation: any planet in any dasha occupied natal house (across samples)
  const anyTransit = Object.values(transits.planetsTransitingDashaOccupied||{})
    .some(arr => (arr||[]).length>0);
  if (anyTransit) acc += ACTIVATION_BONUS;

  return acc;
}

export function aggregateScore(contributors: number[]): number {
  if (!contributors.length) return 0;
  const mean = contributors.reduce((a,b)=>a+b,0) / contributors.length;
  const clamped = Math.max(-1, Math.min(1, mean));
  return Math.round(clamped * 100); // −100..+100
}
// END FILE
